import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';

/**
 * Die Farbe eines Servers oder einer Instanz.
 *
 * Sie trägt keine Bedeutung für die Logik. Sie hilft dem Auge, in einer Tabelle
 * mit zwanzig Zeilen die zusammengehörigen schneller zu finden — mehr soll sie
 * nicht, und deshalb ist sie auch nirgends Pflicht.
 *
 * Bewusst frei wählbar statt aus einer festen Palette: wer sie setzt, weiss
 * besser als wir, was er unterscheiden will. Die Vorschläge unten sind ein
 * Angebot, kein Rahmen.
 */

/** Ein paar Farben, die auf hellem wie dunklem Grund lesbar bleiben. */
export const VORSCHLAEGE = [
  '#2563eb', '#0891b2', '#059669', '#65a30d',
  '#ca8a04', '#ea580c', '#dc2626', '#db2777',
  '#7c3aed', '#475569',
];

/**
 * Macht aus einer Eingabe eine Farbe — oder `null`.
 *
 * Nimmt `2563eb` genauso wie `#2563EB` und die Kurzform `#27b`. Wer eine Farbe
 * aus einer anderen Anwendung kopiert, hat selten genau die Form, die wir
 * erwarten; daran soll es nicht scheitern.
 */
export function alsHex(eingabe: string): string | null {
  const roh = eingabe.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{6}$/.test(roh)) return `#${roh}`;
  // Kurzform aufziehen: #27b → #2277bb
  if (/^[0-9a-f]{3}$/.test(roh)) return `#${roh[0]}${roh[0]}${roh[1]}${roh[1]}${roh[2]}${roh[2]}`;
  return null;
}

/** Der Punkt in der Tabellenzeile. Ohne Farbe ein blasser Ring, kein Loch. */
export function FarbPunkt({ farbe, titel }: { farbe?: string | null; titel?: string }) {
  return (
    <span
      title={titel ?? (farbe ? `Farbe ${farbe}` : 'keine Farbe')}
      aria-hidden
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
      style={
        farbe
          ? { backgroundColor: farbe, borderColor: farbe }
          : { backgroundColor: 'transparent', borderColor: 'var(--c-slate-300, #cbd5e1)' }
      }
    />
  );
}

/**
 * Der Wähler im Formular.
 *
 * `null` bedeutet „keine" und ist ein eigener Knopf, kein leerer Farbwert —
 * eine Farbe zu entfernen muss so einfach sein wie eine zu setzen, sonst bleibt
 * die erste versehentlich gesetzte für immer stehen.
 *
 * ══ WARUM EIN HEX-FELD DANEBEN ════════════════════════════════════════════
 *
 * Zwei Wege, die der Farbwähler des Browsers beide nicht anbietet:
 *
 *   **Hinein** — eine Farbe liegt oft schon als Hex vor, aus dem Corporate
 *   Design des Kunden oder aus einer anderen Anwendung. Sie im Farbrad
 *   nachzuklicken ist Arbeit mit ungewissem Ausgang.
 *
 *   **Heraus** — wer denselben Kunden über mehrere Server und Instanzen hinweg
 *   gleich einfärben will, braucht den Wert zum Weitergeben. Ohne Anzeige
 *   müsste er in der Datenbank nachsehen.
 *
 * Geschrieben wird erst, wenn die Eingabe eine **vollständige** Farbe ergibt.
 * Sonst löste jeder Tastendruck eine Speicherung aus — und `#2` ist keine
 * Farbe, sondern ein halb getippter Gedanke.
 */
export function FarbWaehler({
  wert,
  onWahl,
  onAuswahlFertig,
}: {
  wert?: string | null;
  onWahl: (farbe: string | null) => void;
  /**
   * Ein Klick auf ein fertiges Farbfeld — Vorschlag oder „keine".
   *
   * Nur dort, nicht am Farbrad und nicht beim Tippen: die beiden sind ein
   * Suchen, und ein Panel, das sich beim ersten Tastendruck schliesst, macht
   * aus dem Hex-Feld eine Falle. Ohne Angabe passiert nichts — im Formular
   * gibt es nichts zu schliessen.
   */
  onAuswahlFertig?: () => void;
}) {
  const [eingabe, setEingabe] = useState(wert ?? '');
  const [kopiert, setKopiert] = useState(false);

  // Von aussen gesetzte Werte übernehmen — etwa nach dem Klick auf einen
  // Vorschlag oder wenn das Formular eine andere Zeile lädt.
  useEffect(() => { setEingabe(wert ?? ''); }, [wert]);

  const gueltig = alsHex(eingabe);
  const angetippt = eingabe.trim() !== '';

  function tippen(text: string) {
    setEingabe(text);
    const farbe = alsHex(text);
    if (farbe) onWahl(farbe);
  }

  async function kopieren() {
    if (!wert) return;
    try {
      await navigator.clipboard.writeText(wert);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1200);
    } catch {
      // Ohne Zwischenablage-Recht bleibt der Wert im Feld stehen und lässt sich
      // von Hand markieren. Ein Fehlerhinweis wäre hier mehr Lärm als Hilfe.
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        onClick={() => { setEingabe(''); onWahl(null); onAuswahlFertig?.(); }}
        title="keine Farbe"
        className={`h-6 w-6 rounded-full border-2 text-[10px] leading-none text-slate-400 ${
          wert ? 'border-slate-300' : 'border-slate-900'
        }`}
      >
        ✕
      </button>

      {VORSCHLAEGE.map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => { onWahl(f); onAuswahlFertig?.(); }}
          title={f}
          className={`h-6 w-6 rounded-full border-2 ${
            wert?.toLowerCase() === f ? 'border-slate-900' : 'border-transparent'
          }`}
          style={{ backgroundColor: f }}
        />
      ))}

      <input
        type="color"
        value={wert ?? '#2563eb'}
        onChange={(e) => onWahl(e.target.value)}
        title="Farbrad"
        className="h-6 w-8 cursor-pointer rounded border border-slate-300 bg-surface p-0.5"
      />

      <span className="inline-flex items-center">
        <input
          type="text"
          value={eingabe}
          onChange={(e) => tippen(e.target.value)}
          onBlur={() => setEingabe(wert ?? '')}
          placeholder="#2563eb"
          spellCheck={false}
          aria-label="Farbe als Hex-Wert"
          title="Hex eingeben oder herauskopieren — auch ohne #, auch als Kurzform (#27b)"
          className={`w-24 rounded-l border px-2 py-1 font-mono text-xs focus:outline-none focus:ring-1 ${
            !angetippt || gueltig
              ? 'border-slate-300 focus:ring-brand-400'
              : 'border-amber-400 bg-amber-50 text-amber-800 focus:ring-amber-400'
          }`}
        />
        <button
          type="button"
          onClick={kopieren}
          disabled={!wert}
          title={wert ? `${wert} kopieren` : 'keine Farbe gesetzt'}
          className="rounded-r border border-l-0 border-slate-300 px-1.5 py-1 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
        >
          {kopiert ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
        </button>
      </span>
    </div>
  );
}

/**
 * Der Punkt als Schalter: klicken, Farbe wählen, fertig.
 *
 * ══ WARUM NICHT DER WAEHLER IN DER ZEILE ══════════════════════════════════
 *
 * Der ausgeklappte Wähler ist elf Farbfelder, ein Farbrad und ein Hex-Feld —
 * eine Werkzeugleiste für eine Entscheidung, die man einmal trifft und danach
 * jahrelang nicht mehr anfasst. In der Kopfzeile stand sie deshalb ständig im
 * Weg und drängte die Angaben, die man wirklich liest, in die nächste Reihe.
 *
 * Der Punkt dagegen ist genau das, was man aus der Liste kennt: dieselbe
 * Kennung, an derselben Stelle vor dem Namen. Dass er sich anklicken lässt,
 * macht ihn nicht grösser — es macht nur den Weg kürzer.
 *
 * Bewusst ein echter `<button>` und nicht der `aria-hidden`-Punkt von oben:
 * verschwindet der Wähler aus der Zeile, ist das hier der einzige Weg zur
 * Farbe. Einer, den die Tastatur nicht erreicht, wäre keiner.
 */
export function FarbPunktSchalter({
  wert,
  onWahl,
  titel,
}: {
  wert?: string | null;
  onWahl: (farbe: string | null) => void;
  /** Wofür die Farbe steht — landet in der Beschriftung des Schalters. */
  titel?: string;
}) {
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  // Klick daneben und Esc schliessen. Dasselbe Muster wie im Aktionsmenü, und
  // `mousedown` statt `click`, damit ein Klick ins Leere nicht erst noch als
  // Klick auf etwas anderes ankommt.
  useEffect(() => {
    if (!offen) return;
    function beiZeiger(e: MouseEvent) {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    }
    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') setOffen(false);
    }
    document.addEventListener('mousedown', beiZeiger);
    document.addEventListener('keydown', beiTaste);
    return () => {
      document.removeEventListener('mousedown', beiZeiger);
      document.removeEventListener('keydown', beiTaste);
    };
  }, [offen]);

  const beschriftung = titel
    ? `Farbe von ${titel}${wert ? ` — ${wert}` : ' — keine gesetzt'}`
    : wert
      ? `Farbe ${wert}`
      : 'Farbe wählen';

  return (
    <div ref={huelle} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        aria-haspopup="dialog"
        aria-label={beschriftung}
        title={beschriftung}
        // Ohne gesetzte Farbe bleibt ein deutlich sichtbarer Ring stehen: er ist
        // dann der einzige Hinweis darauf, dass es hier überhaupt etwas zu
        // wählen gibt. Ein blasser Punkt wäre als Einstieg unauffindbar.
        className={`h-4 w-4 shrink-0 rounded-full border-2 transition hover:ring-2 hover:ring-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-400 ${
          wert ? '' : 'border-dashed'
        } ${offen ? 'ring-2 ring-brand-400' : ''}`}
        style={
          wert
            ? { backgroundColor: wert, borderColor: wert }
            : { backgroundColor: 'transparent', borderColor: 'var(--c-slate-400, #94a3b8)' }
        }
      />

      {offen && (
        <div
          role="dialog"
          aria-label={beschriftung}
          className="absolute left-0 top-6 z-30 rounded-xl border border-slate-200 bg-surface p-3 shadow-lg"
        >
          <FarbWaehler wert={wert} onWahl={onWahl} onAuswahlFertig={() => setOffen(false)} />
        </div>
      )}
    </div>
  );
}
